import React from 'react';
import { motion } from 'framer-motion';

const StudentShoppingGuiltLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8">
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Student Shopping Guilt: Managing Your Budget While Still Living Your Life
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Student Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track purchases, balance your student budget, and reduce that post-shopping regret with our specialized student guilt calculator.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Student Purchase
            </motion.button>
          </div>
          <div className="text-8xl">📚</div>
        </div>
      </div>
      
      {/* Student-specific shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Students Face Unique Shopping Guilt</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Limited Income</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Balancing purchases against student loans, part-time jobs, and tight budgets creates extra financial stress.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📱</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Pressure</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Campus life and social media create constant pressure to keep up with trends and appearances.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧠</span>
              <div>
                <h3 className="font-bold dark:text-white">Stress Shopping</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Academic pressure and burnout often lead to retail therapy as a coping mechanism.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏱️</span>
              <div>
                <h3 className="font-bold dark:text-white">Future Anxiety</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Concerns about post-graduation debt and employment prospects intensify spending guilt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Student Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Student-Specific Triggers
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify when exam stress, social events, or campus FOMO lead to impulse spending.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Balance Essential vs. Non-Essential Spending
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn to prioritize textbooks and supplies while still making room for social experiences.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Student-Specific Budget Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive personalized tips that work with semester cycles, student discounts, and campus resources.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Build Smart Habits for Life After Graduation
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Develop financial skills now that will serve you throughout your future career and beyond.
            </p>
          </div>
        </div>
      </section>
      
      {/* Student Shopper Types Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Student Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📱</span>
              <h3 className="font-bold mt-2 dark:text-white">Campus Influencer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Your purchases are driven by social status and keeping up with the latest campus trends, even when your budget says otherwise.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">📚</span>
              <h3 className="font-bold mt-2 dark:text-white">Academic Overwhelmer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You shop as a break from studying, often rationalizing non-essential purchases as "rewards" for academic work.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎮</span>
              <h3 className="font-bold mt-2 dark:text-white">FOMO Fighter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're spending on experiences and items to ensure you don't miss out on the "college experience," regardless of cost.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧮</span>
              <h3 className="font-bold mt-2 dark:text-white">Savvy Scholar</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You've mastered the art of student discounts, coupon stacking, and secondhand finds, making your limited budget stretch further.
            </p>
          </div>
        </div>
      </section>
      
      {/* Student Shopping Stats */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Student Shopping By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">$11k+</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual spending by college students</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">73%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of students work while in school</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">85%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">experience financial stress regularly</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">40%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report retail therapy for stress relief</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">What Students Are Saying</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was spending my entire work-study paycheck on DoorDash and Amazon impulse buys. After tracking with Guilt Gauge for a month, I've saved enough for my spring break trip without touching my student loans."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jordan K.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Junior, Business Major</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "As a first-gen student, I was constantly stressing about every purchase. The student guilt calculator helped me see that some spending on social events was actually important for my college experience, while cutting back in other areas."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Ava M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sophomore, Computer Science</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Student Shopping Guilt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I balance social spending with my student budget?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create a dedicated "social experiences" category in your budget and track it with our student guilt calculator. Look for free campus events, student discounts, and low-cost alternatives like potluck gatherings instead of restaurants.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it wrong to use student loan money for non-educational purchases?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              While student loans are primarily for educational expenses, basic living costs are typically included in the cost of attendance. Track these purchases in our calculator to understand your true needs versus wants, and consider the long-term impact of borrowing for non-essentials.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I resist peer pressure to spend money I don't have?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Our student guilt calculator helps identify social spending triggers. Suggest budget-friendly alternatives, be honest with friends about your financial boundaries, and connect with like-minded peers who share your financial values and goals.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I stop stress-shopping during exams and deadlines?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Use our tracker to identify academic stress triggers, then develop alternative stress-relief strategies like campus gym visits, study breaks with friends, or free mindfulness apps. Create a "finals survival plan" that includes healthy rewards that don't impact your budget.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Balance Your Student Life & Budget</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your student purchases and develop money skills that last well beyond graduation.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Student Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Student Shopping Guilt</h2>
          <p className="mb-3">
            Student shopping guilt is the unique financial anxiety experienced by college and university students who must balance limited income, social pressures, and academic expenses while managing student loans and future financial uncertainty.
          </p>
          <p className="mb-3">
            Our Student Guilt Calculator helps you understand and address these feelings by providing specialized tracking tools and insights designed specifically for the student budget cycle, campus lifestyle, and academic calendar.
          </p>
          <p>
            Whether you're dealing with textbook season splurges, social spending pressure, or stress-induced shopping during finals, our free tool helps you develop healthier financial habits that will serve you through graduation and beyond.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StudentShoppingGuiltLanding;
